import { useTranslation } from 'react-i18next';
import Typography from '@lib/components/Typography/Typography';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import { VerificationCodeInput } from '@lib/components/VerificationCodeInput';
import ControllerErrorRender from '@lib/components/ReactHookForm/ControllerErrorRender';
import LinkComponent, { LinkSizes, LinkTypes } from '@lib/components/Link/Link';
import Trans from '@lib/components/Trans/Trans';
import { Colors } from '@lib/enums/colors';
import { MethodCard } from '../../MethodCard';
import { TwoFactorMethod } from '../types';
import styles from './VerifyIdentityStep.module.scss';

interface MethodOption {
  id: TwoFactorMethod;
  icon: string;
  iconColor: Colors;
  titleKey: string;
  descriptionKey: string;
}

const methodOptions: MethodOption[] = [
  {
    id: 'totp',
    icon: 'smartphone',
    iconColor: Colors.Blue,
    titleKey: 'setup-2fa-authenticator-title',
    descriptionKey: 'use-app-to-generate-code',
  },
  {
    id: 'email',
    icon: 'mail',
    iconColor: Colors.Blue,
    titleKey: 'setup-2fa-email-title',
    descriptionKey: 'send-code-to-your-email',
  },
];

export interface VerifyIdentityStepProps {
  // Method selection props
  showMethodSelection: boolean;
  twoFactorTotpEnabled: boolean;
  twoFactorEmailOtpEnabled: boolean;
  onMethodSelect: (method: TwoFactorMethod) => void;

  // Verification props
  selectedMethod: TwoFactorMethod | null;
  verificationCode: string;
  onCodeChange: (code: string) => void;
  onVerify: () => void;
  isVerifying: boolean;

  // Error handling
  error: string | null;

  // Email resend
  onResendEmail?: () => void;
  resendCountdown?: number;
  isResending?: boolean;

  // Back button (for method selection)
  onBack?: () => void;

  // Recovery mode props
  isRecoveryMode?: boolean;
  onRecoveryModeChange?: (isRecoveryMode: boolean) => void;
}

/**
 * Step 1: Verify Identity
 *
 * This step has two sub-states:
 * 1. Method selection (when both methods enabled)
 * 2. Code verification for selected method
 */
function VerifyIdentityStep({
  showMethodSelection,
  twoFactorTotpEnabled,
  twoFactorEmailOtpEnabled,
  onMethodSelect,
  selectedMethod,
  verificationCode,
  onCodeChange,
  onVerify,
  isVerifying,
  error,
  onResendEmail,
  resendCountdown = 0,
  isResending = false,
  onBack,
  isRecoveryMode = false,
  onRecoveryModeChange,
}: VerifyIdentityStepProps) {
  const { t } = useTranslation();

  const formatCountdown = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes} min ${secs} s`;
  };

  // Filter methods to only show enabled ones
  const availableMethods = methodOptions.filter(
    (method) =>
      (method.id === 'totp' && twoFactorTotpEnabled) ||
      (method.id === 'email' && twoFactorEmailOtpEnabled),
  );

  // Method selection view
  if (showMethodSelection) {
    return (
      <div className={styles.container}>
        <div className={styles.methodsList}>
          {availableMethods.map(
            ({ id, icon, iconColor, titleKey, descriptionKey }) => (
              <MethodCard
                key={id}
                id={id}
                icon={icon}
                iconColor={iconColor}
                titleKey={titleKey}
                descriptionKey={descriptionKey}
                onSetup={(methodId) =>
                  onMethodSelect(methodId as TwoFactorMethod)
                }
                buttonTextKey="verify"
              />
            ),
          )}
        </div>
      </div>
    );
  }

  // Verification view
  if (!selectedMethod) return null;

  const isTotpMethod = selectedMethod === 'totp';
  const isEmailMethod = selectedMethod === 'email';

  // Calculate required code length based on recovery mode
  const requiredCodeLength = isRecoveryMode ? 8 : 6;

  // Unique name per context → unique id → prevents password managers from
  // cross-filling between states. autoComplete="off" signals to skip autofill.
  let inputName = 'authenticator-verification-code-input';
  if (isRecoveryMode) inputName = 'modal-field-tertiary';
  else if (isEmailMethod) inputName = 'modal-field-secondary';

  // Handle entering recovery mode
  const handleEnterRecoveryMode = () => {
    onRecoveryModeChange?.(true);
    onCodeChange(''); // Clear the code when switching modes
  };

  // Handle exiting recovery mode (back button)
  const handleExitRecoveryMode = () => {
    onRecoveryModeChange?.(false);
    onCodeChange(''); // Clear the code when switching modes
  };

  return (
    <div className={styles.container}>
      {!isRecoveryMode && (
        <div className={styles.header}>
          <Typography variant="body">
            {isTotpMethod ? (
              t('type-verification-code')
            ) : (
              <Trans i18nKey="verification-code-sent" />
            )}
          </Typography>
        </div>
      )}

      <div className={styles.verificationContainer}>
        <VerificationCodeInput
          key={inputName}
          value={verificationCode}
          onChange={onCodeChange}
          error={!!error}
          autoFocus
          disabled={isVerifying}
          length={requiredCodeLength}
          alphanumeric={isRecoveryMode}
          onEnter={
            verificationCode.length === requiredCodeLength && !isVerifying
              ? onVerify
              : undefined
          }
          name={inputName}
          autoComplete="off"
        />
        {error && (
          <div className={styles.verificationError}>
            <ControllerErrorRender error={{ message: error, type: 'error' }} />
          </div>
        )}
        {/* Show email resend only when not in recovery mode */}
        {!isRecoveryMode && isEmailMethod && onResendEmail && (
          <div className={styles.resendContainer}>
            {resendCountdown > 0 || isResending ? (
              <Typography variant="caption" className={styles.resendCountdown}>
                {isResending
                  ? t('sending')
                  : `${t('resend-in')} ${formatCountdown(resendCountdown)}`}
              </Typography>
            ) : (
              <LinkComponent
                size={LinkSizes.small}
                type={LinkTypes.secondaryGray}
                onClick={onResendEmail}
              >
                {t('resend-code')}
              </LinkComponent>
            )}
          </div>
        )}
        {/* Show "Lost access to authenticator?" link when not in recovery mode */}
        {!isRecoveryMode && onRecoveryModeChange && (
          <div className={styles.resendContainer}>
            <LinkComponent
              size={LinkSizes.small}
              type={LinkTypes.secondaryGray}
              onClick={handleEnterRecoveryMode}
            >
              {t('lost-access-to-authenticator')}
            </LinkComponent>
          </div>
        )}
      </div>

      <div className={styles.actionsContainer}>
        {/* In recovery mode, show Back button to return to normal verification */}
        {isRecoveryMode && (
          <Button
            buttonText={t('back')}
            buttonType={ButtonTypes.primaryOutlined}
            buttonSize={ButtonSizes.large}
            onClick={handleExitRecoveryMode}
            leftIcon="arrow_back"
            disabled={isVerifying}
            fullWidth
          />
        )}
        {/* Regular Back button (for method selection) - only when not in recovery mode */}
        {!isRecoveryMode && onBack && (
          <Button
            buttonText={t('back')}
            buttonType={ButtonTypes.primaryOutlined}
            buttonSize={ButtonSizes.large}
            onClick={onBack}
            leftIcon="arrow_back"
            disabled={isVerifying}
            fullWidth
          />
        )}
        <Button
          buttonText={t('verify-continue')}
          buttonType={ButtonTypes.primaryFilled}
          buttonSize={ButtonSizes.large}
          onClick={onVerify}
          disabled={
            verificationCode.length !== requiredCodeLength || isVerifying
          }
          isLoading={isVerifying}
          fullWidth={isRecoveryMode || !!(!isRecoveryMode && onBack)}
        />
      </div>
    </div>
  );
}

export default VerifyIdentityStep;
